import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { submitFeedbackApi } from "../api";

const CATEGORIES = [
  { key: "question",    labelKey: "feedback.cat_question",    icon: "bi-patch-question",   color: "primary" },
  { key: "suggestion",  labelKey: "feedback.cat_suggestion",  icon: "bi-lightbulb",        color: "warning" },
  { key: "bug",         labelKey: "feedback.cat_bug",         icon: "bi-bug",              color: "danger" },
  { key: "partnership", labelKey: "feedback.cat_partnership", icon: "bi-handshake",        color: "success" },
  { key: "other",       labelKey: "feedback.cat_other",       icon: "bi-three-dots",       color: "secondary" },
];

export default function FeedbackModal({ open, onClose }) {
  const { t } = useTranslation();
  const { user, company } = useAuth();
  const [form, setForm] = useState({
    name: "", email: "", company: "",
    category: "question", subject: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setErr(""); setDone(false);
      setForm({
        name: user?.name || "",
        email: user?.email || "",
        company: company?.name || "",
        category: "question", subject: "", message: "",
      });
    }
  }, [open, user, company]);

  if (!open) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await submitFeedbackApi({
        name: form.name.trim() || null,
        email: form.email.trim() || null,
        company: form.company.trim() || "",
        category: form.category,
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || t("feedback.submit_error"));
    } finally { setBusy(false); }
  };

  const isLoggedIn = !!user;

  return (
    <div className="modal d-block" tabIndex="-1"
         style={{ background: "rgba(0,0,0,0.5)", overflowY: "auto" }}
         data-testid="feedback-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-dialog modal-dialog-centered modal-lg" style={{ marginTop: 24, marginBottom: 24 }}>
        <div className="modal-content" style={{ maxHeight: "calc(100vh - 48px)" }}>
          <div className="modal-header" style={{ flexShrink: 0 }}>
            <h5 className="modal-title">
              <i className="bi bi-headset me-2"></i>
              {t("feedback.title")}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}
                    data-testid="feedback-close"></button>
          </div>

          {done ? (
            <div className="modal-body text-center py-5" style={{ overflowY: "auto" }}>
              <div className="mb-3">
                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: 64 }}></i>
              </div>
              <h5 className="fw-bold mb-2">{t("feedback.sent_title")}</h5>
              <p className="text-muted mb-4">{t("feedback.sent_text")}</p>
              <button className="btn btn-primary" onClick={onClose}
                      data-testid="feedback-done-btn">
                {t("common.close")}
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", minHeight: 0, flexGrow: 1 }}>
              <div className="modal-body" style={{ overflowY: "auto", flexGrow: 1, padding: "1.25rem 1.5rem" }}>
                {err && <div className="alert alert-danger py-2">{err}</div>}

                {!isLoggedIn && (
                  <div className="alert alert-light py-2 small">
                    <i className="bi bi-info-circle me-1"></i>
                    {t("feedback.guest_hint")}
                  </div>
                )}

                <label className="form-label small fw-semibold mb-2">{t("feedback.category_label")}</label>
                <div className="d-flex flex-wrap gap-2 mb-3" data-testid="feedback-categories">
                  {CATEGORIES.map(c => (
                    <button type="button" key={c.key}
                            className={`btn btn-sm ${form.category === c.key ? `btn-${c.color}` : `btn-outline-${c.color}`}`}
                            onClick={() => setForm({ ...form, category: c.key })}
                            data-testid={`feedback-cat-${c.key}`}>
                      <i className={`bi ${c.icon} me-1`}></i>{t(c.labelKey)}
                    </button>
                  ))}
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small">{t("feedback.your_name")}</label>
                    <input className="form-control" value={form.name}
                           onChange={(e) => setForm({ ...form, name: e.target.value })}
                           placeholder={isLoggedIn ? "" : t("feedback.your_name_ph")}
                           data-testid="feedback-name" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">{t("feedback.email_for_reply")} {!isLoggedIn && <span className="text-danger">*</span>}</label>
                    <input type="email" className="form-control" value={form.email}
                           onChange={(e) => setForm({ ...form, email: e.target.value })}
                           required={!isLoggedIn}
                           data-testid="feedback-email" />
                  </div>
                  <div className="col-12">
                    <label className="form-label small">{t("feedback.company")}</label>
                    <input className="form-control" value={form.company}
                           onChange={(e) => setForm({ ...form, company: e.target.value })}
                           data-testid="feedback-company" />
                  </div>
                  <div className="col-12">
                    <label className="form-label small">{t("feedback.subject")} <span className="text-danger">*</span></label>
                    <input className="form-control" value={form.subject}
                           onChange={(e) => setForm({ ...form, subject: e.target.value })}
                           required minLength={1} maxLength={200}
                           placeholder={t("feedback.subject_ph")}
                           data-testid="feedback-subject" />
                  </div>
                  <div className="col-12">
                    <label className="form-label small">{t("feedback.message")} <span className="text-danger">*</span></label>
                    <textarea className="form-control" value={form.message} rows={6}
                              onChange={(e) => setForm({ ...form, message: e.target.value })}
                              required minLength={5} maxLength={5000}
                              placeholder={t("feedback.message_ph")}
                              data-testid="feedback-message" />
                    <div className="text-muted small mt-1" style={{ fontSize: 11 }}>
                      {t("feedback.chars_count", { n: form.message.length })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer" style={{ flexShrink: 0, padding: "1rem 1.5rem", gap: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
                <button type="submit" className="btn btn-primary" disabled={busy}
                        data-testid="feedback-submit-btn">
                  <i className="bi bi-send me-1"></i>
                  {busy ? t("feedback.sending") : t("feedback.submit")}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
